import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyLayout from "./PolicyLayout";

const email = siteConfig.contact.email;

const policies = {
  orders: {
    title: "Orders & cancellations",
    intro: "How an order is placed, confirmed, changed, substituted or cancelled.",
    sections: [
      {
        title: "Placing an order",
        body: [
          "Review the products, quantities, delivery address, delivery option and total before submitting checkout. You are responsible for providing complete and accurate information and for being authorised to use the selected payment method.",
          "An on-screen or email acknowledgement means that we received your request. It does not by itself mean that we have accepted the order.",
        ],
      },
      {
        title: "Order acceptance",
        body: [
          "An order is accepted when we send a fulfilment or dispatch confirmation. Before acceptance, we may decline or limit an order because of unavailable stock, a payment issue, an obvious price or description error, a delivery restriction, suspected fraud or a breach of our terms.",
          "If we cannot accept an order after payment has been authorised or captured, we will void the authorisation or arrange a refund for the affected amount.",
        ],
      },
      {
        title: "Changes and cancellations",
        body: [
          `Email ${email} as soon as possible with your order reference. We will try to help, but a change or cancellation is not guaranteed once picking, cutting, personalisation, chilling or dispatch has begun.`,
          "If a requested address or delivery change increases the delivery cost, we will tell you the additional amount before confirming the change.",
        ],
      },
      {
        title: "Unavailable items and substitutions",
        body: [
          "Stock can change between checkout and fulfilment. We will not make a materially different substitution without your agreement, particularly where ingredients, allergens, origin, dietary status or price differ.",
          "If you decline a proposed substitution, we will remove the unavailable item and refund any amount paid for it. A partial cancellation does not normally cancel the rest of the order.",
        ],
      },
      {
        title: "Quantity limits and resale",
        body: [
          "We may apply reasonable per-customer or per-address limits to scarce, promotional or perishable products. Unless we agree otherwise in writing, products are offered for personal use and not commercial resale.",
        ],
      },
      {
        title: "Checking your confirmation",
        body: [
          `Check the confirmation promptly and report an incorrect item, quantity, address or charge to ${email}. Keep the confirmation and order reference until the order and any return or refund are complete.`,
        ],
      },
    ],
  },
  payments: {
    title: "Payments & billing",
    intro: "How payment authorisation, charges, currency, refunds and payment disputes work.",
    sections: [
      {
        title: "Payment processing",
        body: [
          "Checkout redirects to the payment provider presented at the time of purchase. That provider processes card and payment credentials under its own terms and privacy notice. We do not intentionally store complete card numbers or card security codes in this storefront.",
          "Use only a payment method that you are authorised to use. Your bank or provider may apply its own verification steps, limits, fees or exchange rate.",
        ],
      },
      {
        title: "Authorisation and capture",
        body: [
          "Submitting payment may place an authorisation hold on your account. A hold is not always a completed charge and its release time is controlled by your payment provider. Payment must be successfully authorised before we can fulfil an order.",
        ],
      },
      {
        title: "Currency and total",
        body: [
          `The checkout total is shown in ${siteConfig.currency.code} and includes the displayed product prices, delivery charge and applied discounts. If your account uses another currency, your provider may convert the amount and charge a conversion or cross-border fee.`,
          "We will not add a new store charge after checkout without your agreement. Duties or taxes charged directly by a government, carrier or payment provider are addressed separately in our International orders policy.",
        ],
      },
      {
        title: "Failed, pending or duplicate payments",
        body: [
          `If payment fails or remains pending, check your provider before trying again. If two completed charges appear for the same order, send the order reference and non-sensitive transaction details to ${email}. Do not email a complete card number or security code.`,
        ],
      },
      {
        title: "Refunds",
        body: [
          "Approved refunds are returned to the original payment method unless the law requires another approach. We issue the refund amount promptly after approval, but the date it appears is controlled by the payment networks and your provider.",
          "Currency conversion, bank fees and exchange-rate movements are outside our control. We do not refund third-party fees unless required by law.",
        ],
      },
      {
        title: "Chargebacks and payment disputes",
        body: [
          `Contact us first if you do not recognise a charge or have a problem with an order so we can investigate quickly. This does not restrict your right to contact your payment provider. We may give the provider relevant order, delivery and correspondence records when responding to a payment dispute.`,
        ],
      },
    ],
  },
  products: {
    title: "Product information & pricing",
    intro: "How we present product details, natural variation, availability and prices.",
    sections: [
      {
        title: "Product descriptions",
        body: [
          "We aim to keep names, ingredients, origins, dietary information, pack sizes, storage guidance and other descriptions accurate and current. Supplier recipes and packaging can change, so the label delivered with the product is the final source for ingredients, allergens, dates and storage instructions.",
        ],
      },
      {
        title: "Images and natural variation",
        body: [
          "Images illustrate the product but packaging, colour and appearance may vary. Artisan and fresh products naturally differ in shape, rind, marbling, ripeness and colour; these differences are not necessarily defects.",
        ],
      },
      {
        title: "Weights and portions",
        body: [
          "A hand-cut or naturally sized item may have an approximate weight. Where a listing gives a range or approximate measure, the supplied portion may vary within that description. Contact us before ordering if an exact quantity is essential for your intended use.",
        ],
      },
      {
        title: "Prices and taxes",
        body: [
          `Prices are displayed in ${siteConfig.currency.code} unless clearly stated otherwise. The final product price, discount and delivery fee are shown before payment. A crossed-out or comparison price is used only where we have a reasonable basis for the comparison.`,
        ],
      },
      {
        title: "Obvious errors",
        body: [
          "If a price, discount, image or description contains an obvious error, we may correct it before accepting the order. If payment has already been taken, we will ask whether you want the corrected product or cancel and refund the affected item.",
        ],
      },
      {
        title: "Availability and limits",
        body: [
          "All products are subject to availability. Adding an item to a cart does not reserve it. We may limit quantities fairly to manage scarce stock, maintain freshness or prevent misuse of a promotion.",
        ],
      },
    ],
  },
  foodSafety: {
    title: "Food safety & allergens",
    intro: "Important information for selecting, receiving, storing and consuming food safely.",
    sections: [
      {
        title: "Allergen information",
        body: [
          "Always read the ingredient and allergen statements on the packaging delivered, even if you have bought the product before. Recipes, suppliers and production methods can change after online information is prepared.",
          `If you have an allergy, intolerance or other dietary restriction, contact ${email} before ordering and identify the specific product and concern. We cannot provide medical advice or guarantee that a product is suitable for an individual condition.`,
        ],
        items: [
          "Common regulated allergens can include cereals containing gluten, crustaceans, eggs, fish, peanuts, soybeans, milk, tree nuts and sulphites at regulated levels.",
          "Products may be produced, cut, stored or packed in facilities that also handle other allergens.",
          "A dietary label such as vegetarian, vegan or gluten-free should be checked against the current packaging and your own requirements.",
        ],
      },
      {
        title: "Chilled delivery and handover",
        body: [
          "Arrange for prompt receipt of chilled or frozen goods. Inspect the parcel on arrival, follow the product label and place temperature-sensitive items into suitable refrigeration or freezing without delay.",
          "Do not consume a product if the packaging is damaged, the seal is broken unexpectedly, the temperature appears unsafe or there are signs of spoilage. Photograph the item and contact us promptly.",
        ],
      },
      {
        title: "Best-before and use-by dates",
        body: [
          "Follow the date and storage directions on the product. A use-by date relates to safety and should not be exceeded. A best-before date generally relates to quality, but the product must still have been stored correctly and show no signs of spoilage.",
        ],
      },
      {
        title: "Preparation and vulnerable consumers",
        body: [
          "Cook, handle and serve food hygienically. Pregnant people, young children, older adults and people with weakened immune systems may need to avoid certain raw, unpasteurised or ready-to-eat products and should seek advice from a qualified health professional.",
        ],
      },
      {
        title: "Recalls and safety notices",
        body: [
          "If a supplier or authority announces a recall or safety alert affecting a product we supplied, we may use available order contact details to notify affected customers and provide instructions. Stop using the product and follow the recall notice.",
        ],
      },
      {
        title: "Reporting a concern",
        body: [
          `For a suspected product safety issue, email ${email} with the order reference, product name, batch or date marking, photographs and a description of the storage conditions. For a medical emergency, contact local emergency services rather than waiting for an email reply.`,
        ],
      },
    ],
  },
  international: {
    title: "International orders",
    intro: "What to know when an order crosses a border or is delivered outside the store's home market.",
    sections: [
      {
        title: "Destination availability",
        body: [
          "A country appearing in an address selector does not guarantee that every product or delivery method is available there. The accepted destination, products and service are those confirmed at checkout and in the order confirmation.",
        ],
      },
      {
        title: "Import restrictions",
        body: [
          "Food, animal products, plants, alcohol and other regulated goods can be restricted by destination. We may remove an item, decline an order or request further information where a shipment would breach carrier or legal requirements.",
          "You are responsible for checking personal-import rules that apply at the delivery destination unless we expressly agree to act as importer of record.",
        ],
      },
      {
        title: "Duties, taxes and fees",
        body: [
          "Unless checkout expressly states that duties and import taxes are included, the recipient is responsible for amounts charged by customs, tax authorities or the carrier. These charges are not controlled by us and may be due before delivery.",
        ],
      },
      {
        title: "Customs information",
        body: [
          "We and our delivery providers may supply accurate product, value, origin and recipient information for customs clearance. Do not ask us to mark an order as a gift or declare an inaccurate value or description.",
        ],
      },
      {
        title: "Delays, refusal and return",
        body: [
          "Customs inspection can delay delivery. If a shipment is refused, abandoned or returned because the recipient did not pay a disclosed charge, provide required information or comply with import rules, any refund may be reduced by non-recoverable shipping, return, disposal or government charges to the extent permitted by law.",
          "Perishable products may be unable to return safely and may have to be disposed of by the carrier or authority.",
        ],
      },
      {
        title: "Currency",
        body: [
          `Orders are charged in ${siteConfig.currency.code}. Your payment provider determines any conversion rate or foreign-transaction fee.`,
        ],
      },
    ],
  },
  promotions: {
    title: "Promotions & discount codes",
    intro: "The rules that apply to promotional prices, offers and discount codes.",
    sections: [
      {
        title: "Promotion details",
        body: [
          "Each promotion is subject to the terms presented with it, including its dates, eligible products, eligible customers, territory, minimum spend and any redemption limit. Times refer to the store's local time unless stated otherwise.",
        ],
      },
      {
        title: "Using a code",
        body: [
          "Enter an eligible code before placing the order and confirm that the discount appears in the checkout total. A code cannot normally be added after an order is placed. Unless the offer says otherwise, only one code may be used per order and codes cannot be exchanged for cash.",
        ],
      },
      {
        title: "Exclusions",
        body: [
          "Delivery fees, already-discounted goods, limited releases, bundles or specified brands may be excluded. A minimum-spend threshold is calculated after excluded items and other discounts unless the promotion states a different method.",
        ],
      },
      {
        title: "Returns and cancellations",
        body: [
          "A refund reflects the amount actually paid for the returned item after allocating any order-level discount. If a return means the remaining order no longer qualifies for a promotion, we may adjust the refund only where that consequence was clearly disclosed and the law allows it.",
        ],
      },
      {
        title: "Fair use",
        body: [
          "Promotions are intended for genuine customer purchases. We may reject redemptions involving automated activity, multiple accounts created to avoid limits, resale, tampering or other abuse. This does not affect orders already validly accepted unless cancellation is permitted by our terms or law.",
        ],
      },
      {
        title: "Errors and changes",
        body: [
          "We may correct an obvious promotional error or withdraw an offer before an order is accepted. We will not retrospectively remove a valid discount from an accepted order. If an error affected your decision, contact customer care for review.",
        ],
      },
    ],
  },
  dataRights: {
    title: "Data rights & marketing choices",
    intro: "How to make a privacy request and control direct-marketing communications.",
    sections: [
      {
        title: "Your request options",
        body: [
          "Subject to the law that applies, you may ask whether we hold personal information about you and request access, correction, deletion, restriction, objection or a portable copy. Some rights apply only in particular circumstances and legal exceptions may allow or require us to keep certain records.",
        ],
      },
      {
        title: "How to submit a request",
        body: [
          `Email ${email} with the subject “Privacy request”. Describe the request and provide the email address or order reference connected with the information. Do not send identity documents until we explain a secure way to provide any verification that is reasonably required.`,
        ],
      },
      {
        title: "Verification and authorised representatives",
        body: [
          "We may ask for information needed to confirm identity and protect the account from unauthorised disclosure. An authorised representative may be asked to provide evidence of authority, and we may still verify the request directly with the individual where the law permits.",
        ],
      },
      {
        title: "Direct marketing",
        body: [
          "We will use personal information for direct marketing only after giving the required notice and obtaining any consent required by applicable law. Declining marketing does not prevent service messages about an order, security issue, policy update or product recall.",
          `You may withdraw a marketing consent at any time by using the unsubscribe method in the message or emailing ${email}. We do not charge for opting out.`,
        ],
      },
      {
        title: "Response and retention",
        body: [
          "We aim to acknowledge requests promptly and respond within the period required by applicable law. We may keep a limited record of the request and response to demonstrate compliance, prevent fraud and avoid sending marketing after an opt-out.",
        ],
      },
      {
        title: "Questions and complaints",
        body: [
          "If you are dissatisfied with our response, reply to the case so it can be reviewed. You may also complain to the privacy or data-protection authority that has jurisdiction over the matter.",
        ],
      },
    ],
  },
  account: {
    title: "Account policy",
    intro: "The rules for customer accounts, guest checkout and saved browser preferences.",
    sections: [
      {
        title: "Accounts and guest shopping",
        body: [
          "Where account features are available, you may be able to view orders, save delivery details or manage favourites. Guest checkout may also be offered. Creating an account does not guarantee access to every feature, product or promotion.",
        ],
      },
      {
        title: "Accurate information",
        body: [
          "Keep your name, email and delivery information accurate and current. Do not impersonate another person, create an account with information you are not authorised to use or use an account for unlawful activity.",
        ],
      },
      {
        title: "Account security",
        body: [
          "Keep sign-in links, passwords and devices secure. Do not forward a one-time sign-in link. Tell us promptly if you suspect unauthorised access, and sign out of shared devices when account access is available.",
        ],
      },
      {
        title: "Cart and favourites",
        body: [
          "This storefront may save cart and favourite identifiers in local browser storage. They can remain on the device without an account and may be cleared through browser settings. Saved items are not reserved and their price and availability can change.",
        ],
      },
      {
        title: "Suspension and closure",
        body: [
          "We may restrict an account where reasonably necessary to protect customers, investigate fraud, comply with law or address a serious breach of our terms. Where appropriate, we will explain the reason and provide a way to ask for review.",
          `To request account closure, email ${email}. Closing an account does not require us to delete records that must be retained for orders, tax, accounting, safety, fraud prevention or legal claims.`,
        ],
      },
    ],
  },
  security: {
    title: "Security & fraud prevention",
    intro: "How we help protect the storefront and what to do if something looks wrong.",
    sections: [
      {
        title: "Our approach",
        body: [
          "We use reasonable technical and organisational measures appropriate to the information and service, including access controls, service monitoring and specialist payment processing. No website, transmission or storage method can be guaranteed completely secure.",
        ],
      },
      {
        title: "Payment security",
        body: [
          "Payment credentials are entered with the payment provider shown during checkout. We do not ask customers to email complete card numbers, card security codes or online-banking passwords. Treat any message requesting them as suspicious.",
        ],
      },
      {
        title: "Fraud checks",
        body: [
          "We and our service providers may use order, device, payment and delivery signals to detect fraud, abuse and unauthorised transactions. We may pause, verify, limit or cancel a suspicious order and may share necessary information with payment providers, carriers, professional advisers or authorities as permitted by law.",
        ],
      },
      {
        title: "Protect yourself",
        body: [
          "Check that you are using the correct website, keep devices and browsers updated, use unique sign-in credentials where applicable and avoid payment over untrusted networks. Be cautious of urgent requests to pay outside the normal checkout.",
        ],
      },
      {
        title: "Report suspicious activity",
        body: [
          `Forward the non-sensitive details of a suspicious message, order or account event to ${email}. Do not include passwords or full payment credentials. If your payment method may be compromised, contact its provider immediately.`,
        ],
      },
      {
        title: "Security research",
        body: [
          `If you believe you found a vulnerability, email ${email} with reproducible details and allow reasonable time for review. Do not access other people's data, disrupt service, use social engineering, demand payment as a condition of disclosure or publicly disclose an unresolved issue.`,
        ],
      },
    ],
  },
  acceptableUse: {
    title: "Acceptable use policy",
    intro: "Rules that protect customers, rights holders and the reliable operation of the website.",
    sections: [
      {
        title: "Permitted use",
        body: [
          "You may browse the website, manage your own shopping activity and contact customer care for lawful personal or authorised business purposes, subject to our terms and these rules.",
        ],
      },
      {
        title: "Prohibited activity",
        body: ["You must not use the website to:"],
        items: [
          "Break the law, infringe intellectual-property, privacy or other rights, or facilitate harm.",
          "Submit false payment, identity, delivery, review or support information.",
          "Introduce malware, probe security without permission, bypass access controls or disrupt service.",
          "Use bots, scraping or automated requests at a volume that harms the service or circumvents purchase limits.",
          "Copy, frame or republish protected content in a misleading, deceptive or commercially exploitative way.",
          "Harass staff or other users, send spam or upload unlawful, discriminatory, threatening or obscene material.",
        ],
      },
      {
        title: "Submitted material",
        body: [
          "If you send a review, image, suggestion or other material, you must have the right to send it and it must be accurate in all material respects. Do not include another person's confidential information or personal data without authority.",
        ],
      },
      {
        title: "Enforcement",
        body: [
          "We may block an abusive request, remove unlawful material, limit features, cancel an unaccepted order or suspend access where proportionate. Serious suspected misconduct may be preserved and reported to affected providers, rights holders or authorities where permitted or required by law.",
        ],
      },
      {
        title: "Reporting misuse",
        body: [
          `Report suspected misuse to ${email} with the relevant page, date and a clear description. Do not attempt to investigate by accessing data or systems without permission.`,
        ],
      },
    ],
  },
  intellectualProperty: {
    title: "Intellectual property policy",
    intro: "Ownership, permitted use and how rights holders can report a concern.",
    sections: [
      {
        title: "Our materials",
        body: [
          `The ${siteConfig.brandName} name, original text, layout, graphics, logos and other store materials are owned by us or used with permission and are protected by applicable intellectual-property laws. No ownership right passes to you by using the website or buying a product.`,
        ],
      },
      {
        title: "Limited permission",
        body: [
          "You may view the website and make a reasonable number of copies of order confirmations or policy pages for your personal records. Any other reproduction, adaptation, distribution, public display, data extraction or commercial use requires prior written permission unless the law expressly allows it.",
        ],
      },
      {
        title: "Third-party rights",
        body: [
          "Product names, packaging, certification marks and third-party trademarks belong to their respective owners. Their appearance identifies the relevant product or source and does not imply an endorsement, partnership or transfer of rights unless expressly stated.",
        ],
      },
      {
        title: "No infringing submissions",
        body: [
          "Do not send or upload content that you copied without permission or that infringes copyright, trademark, design, publicity, privacy or other rights. You remain responsible for material you submit.",
        ],
      },
      {
        title: "Rights-holder notice",
        body: [
          `A rights holder or authorised representative may send a notice to ${email}. Include your contact details, the protected work or right, the exact website location, why you believe the use is unauthorised, and a statement that the notice is accurate and submitted in good faith.`,
          "We may request supporting information, restrict material while reviewing it, contact the person who supplied it and take appropriate action. A knowingly false notice may cause harm, so seek independent advice if ownership or permitted use is uncertain.",
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility statement",
    intro: "Our commitment to making the shopping experience usable by as many people as practical.",
    sections: [
      {
        title: "Our commitment",
        body: [
          "We aim to provide a website that is perceivable, operable, understandable and robust for customers using different devices and assistive technologies. Accessibility is an ongoing part of design, content and maintenance rather than a one-time claim.",
        ],
      },
      {
        title: "Current measures",
        body: [
          "The site uses semantic headings, labelled controls, keyboard-accessible navigation, visible focus handling, descriptive page titles, alternative text where images communicate meaning and a skip link to main content. Responsive layouts support enlargement and smaller screens.",
        ],
      },
      {
        title: "Known limitations",
        body: [
          "Some third-party payment or delivery experiences are controlled by their providers and may not behave exactly like this website. Product packaging images may also contain text that is difficult to read on screen; contact us for the available product information in another format.",
        ],
      },
      {
        title: "Getting assistance",
        body: [
          `If a page, control or document prevents you from completing a task, email ${email}. Tell us the page, the task, the device or assistive technology used and the format or support that would help. We will make a reasonable effort to provide an accessible alternative.`,
        ],
      },
      {
        title: "Feedback",
        body: [
          "We welcome specific accessibility feedback and prioritise issues that block shopping, payment, policy access or customer support. This statement will be updated as meaningful improvements or known limitations change.",
        ],
      },
    ],
  },
  complaints: {
    title: "Complaints & dispute resolution",
    intro: "How to raise a concern and the steps we take to reach a fair outcome.",
    sections: [
      {
        title: "Contact us first",
        body: [
          `Email ${email} with “Complaint” and your order reference in the subject line. Explain the issue, the outcome you are seeking and any relevant dates. Attach clear photographs where the concern involves a product, parcel or label.`,
          "Do not include complete payment-card details, passwords or unrelated sensitive information.",
        ],
      },
      {
        title: "Our review",
        body: [
          "We aim to acknowledge a complaint within two business days. We may ask for reasonable supporting information and consult the payment provider, carrier or supplier. We will keep you informed if the review needs more time.",
        ],
      },
      {
        title: "Possible outcomes",
        body: [
          "Depending on the facts and applicable policy, an outcome may include an explanation, correction, replacement, redelivery, credit, partial refund or full refund. Food-safety concerns may also require product isolation, supplier review or a recall process.",
        ],
      },
      {
        title: "Escalation",
        body: [
          "If you disagree with the initial response, reply with the points you believe were missed and ask for a second review. A person not responsible for the first response will review it where practical.",
        ],
      },
      {
        title: "External options",
        body: [
          "Nothing in this process removes rights or remedies that cannot lawfully be excluded. Depending on the issue and your location, you may contact the relevant consumer, privacy, food-safety, payment or court authority. We may agree to mediation or another proportionate dispute process, but do not require confidential arbitration unless separately and lawfully agreed.",
        ],
      },
      {
        title: "Records and conduct",
        body: [
          "We keep complaint records only as reasonably needed for support, safety, fraud prevention, legal compliance and service improvement. We expect communications on both sides to remain accurate and respectful; abusive conduct does not remove a genuine consumer right, but we may limit the channel used for contact.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Website disclaimer",
    intro: "Important context for general information, product guidance and third-party services.",
    sections: [
      {
        title: "General information",
        body: [
          "Website content is provided for general shopping information. We work to keep it accurate and available, but content may be updated and the website may occasionally be interrupted, delayed or contain an error.",
        ],
      },
      {
        title: "Product and dietary information",
        body: [
          "Online descriptions do not replace the current product label. Always check the delivered ingredients, allergens, date mark, origin and storage instructions. Pairing ideas, serving suggestions and editorial content are opinions, not medical, nutritional or professional advice.",
        ],
      },
      {
        title: "Availability and results",
        body: [
          "Showing a product, promotion, delivery estimate or account feature does not guarantee that it will remain available. Taste, ripeness, appearance and preparation results vary, particularly for natural and artisan products.",
        ],
      },
      {
        title: "Third-party services and links",
        body: [
          "A payment provider, carrier or external website is responsible for its own service, content, availability, accessibility, terms and privacy practices. A link is provided for convenience and does not imply endorsement unless expressly stated.",
        ],
      },
      {
        title: "Limits that apply",
        body: [
          "To the fullest extent permitted by law, we are not responsible for loss caused solely by reliance on outdated third-party information, an event outside reasonable control or misuse of the website. The Terms & conditions contain the contractual liability provisions that apply to an order.",
          "Nothing in this disclaimer excludes a warranty, liability or consumer right that applicable law does not allow us to exclude or limit.",
        ],
      },
      {
        title: "Questions",
        body: [`If important information is unclear, contact ${email} before relying on it or placing an order.`],
      },
    ],
  },
};

const CommercePolicy = ({ policy }) => {
  const content = policies[policy];
  return (
    <PolicyLayout
      eyebrow="Policy center"
      title={content.title}
      intro={content.intro}
      sections={content.sections}
    />
  );
};

export default CommercePolicy;

